import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dtmfProfiles, type DtmfListRow, type NodeTypeCatalogEntry, type DtmfMap,
} from '@/api/dtmfProfiles';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, RotateCcw, Info } from 'lucide-react';
import { useAuth } from '@/lib/auth-store';
import { useCustomerContext } from '@/lib/customer-context-store';

/** Keypad layout — row-major ordering so the grid reads like a phone. */
const KEYPAD: string[][] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

/** Used by the per-type form: translate the "source" label into a friendly name. */
function SOURCE_LABEL(source: string): string {
  if (source === 'customer') return 'Customer override';
  if (source === 'reseller') return 'Reseller override';
  if (source === 'platform') return 'Platform default';
  if (source === 'caller')   return 'Caller override';
  return 'Code default';
}

/**
 * Single `/settings/dtmf-profiles` URL. Renders the list of node types
 * with an inline editor per type. Role determines write scope server-side;
 * the UI lets the current user override whatever their parent tier
 * defined. Super-admin drilling into a reseller via
 * /resellers/:id/settings/dtmf-profiles uses the same component with a
 * resellerId prop to scope reads+writes.
 */
interface Props {
  /** Super-admin drill-in target reseller (renders as reseller view). */
  resellerId?: string;
  /** Super-admin / reseller drill-in target customer (renders as customer view). */
  customerId?: string;
}

export function DtmfProfilesPage({ resellerId, customerId }: Props = {}) {
  const user = useAuth((s) => s.user);
  const qc = useQueryClient();
  // When the customer-scoped variant is reached via the entered-customer
  // context (sidebar link for SA / reseller_owner), show the customer's
  // NAME in the eyebrow instead of a raw uuid prefix. Falls back to the
  // uuid slice when the page is opened for a customer outside the current
  // context (e.g. a direct /customers/:id/... link).
  const contextCustomer = useCustomerContext((s) => s.current);
  const customerLabel =
    customerId && contextCustomer?.id === customerId
      ? contextCustomer.name
      : customerId
        ? `Customer ${customerId.slice(0, 8)}…`
        : null;

  const scopeKey = ['dtmf-profiles', 'list', resellerId ?? null, customerId ?? null];

  const catalogQ = useQuery({
    queryKey: ['dtmf-profiles', 'catalog'],
    queryFn: () => dtmfProfiles.catalog(),
  });

  const listQ = useQuery({
    queryKey: scopeKey,
    queryFn: () => dtmfProfiles.list({ reseller_id: resellerId, customer_id: customerId }),
  });

  const catalog = catalogQ.data?.node_types ?? [];
  const rows = listQ.data?.rows ?? [];

  const catalogByType = useMemo(() => {
    const m = new Map<string, NodeTypeCatalogEntry>();
    for (const c of catalog) m.set(c.node_type, c);
    return m;
  }, [catalog]);

  const scopeTitle =
    customerId ? 'customer overrides'
    : resellerId ? 'reseller overrides'
    : user?.is_super_admin ? 'platform defaults'
    : user?.role === 'reseller_owner' || user?.role === 'agent' ? 'reseller overrides'
    : 'customer overrides';

  const viewerTier =
    customerId || user?.role === 'office_manager' ? 'customer'
    : resellerId || user?.role === 'reseller_owner' || user?.role === 'agent' ? 'reseller'
    : 'owner';

  const description =
    viewerTier === 'owner'
      ? 'Decide which DTMF key does what across the platform, for every IVR node type that takes caller input. Resellers and customers can override these.'
      : viewerTier === 'reseller'
        ? 'Override the platform defaults for this reseller. Every customer under this reseller inherits these settings unless they set their own.'
        : 'Override the reseller defaults for this customer. IVR nodes in this account will follow these mappings.';

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: scopeKey });
  };

  return (
    <>
      <PageHeader
        eyebrow={customerLabel ?? (resellerId ? `Reseller ${resellerId.slice(0, 8)}…` : 'Settings')}
        title="DTMF profiles"
        description={description}
      />

      <Card className="mb-4 border-l-4 border-l-blue-400 bg-blue-50/40">
        <CardContent className="py-3.5 flex items-start gap-3">
          <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900 space-y-1">
            <p>
              Each row below is one IVR node type. Keys you leave unchanged inherit from the level above —
              a blank override here means the caller experiences whatever their {viewerTier === 'owner' ? 'tree' : viewerTier === 'reseller' ? 'platform' : 'reseller'} default is.
            </p>
            <p className="text-blue-800/80">
              Menu nodes aren't shown here — they use the tree's outgoing edges, not a DTMF profile.
            </p>
          </div>
        </CardContent>
      </Card>

      {catalogQ.isLoading || listQ.isLoading ? (
        <Card><CardContent className="py-10 text-sm text-muted-foreground">Loading…</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-10 text-sm text-muted-foreground">No node types available.</CardContent></Card>
      ) : (
        <div className="space-y-4" data-testid="dtmf-profiles-list">
          {rows.map((row) => {
            const entry = catalogByType.get(row.node_type);
            if (!entry) return null;
            return (
              <NodeTypeEditor
                key={row.node_type}
                row={row}
                entry={entry}
                viewerTier={viewerTier}
                scopeTitle={scopeTitle}
                resellerId={resellerId}
                customerId={customerId}
                onSaved={invalidate}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Per-node-type editor ─────────────────────────────────────────────────────

interface EditorProps {
  row: DtmfListRow;
  entry: NodeTypeCatalogEntry;
  viewerTier: 'owner' | 'reseller' | 'customer';
  scopeTitle: string;
  resellerId?: string;
  customerId?: string;
  onSaved: () => void;
}

function NodeTypeEditor({
  row, entry, viewerTier, resellerId, customerId, onSaved,
}: EditorProps) {
  const [expanded, setExpanded] = useState(false);
  const initial = row.my_override ?? {};
  const [draft, setDraft] = useState<DtmfMap>(initial);
  const [err, setErr] = useState<string | null>(null);

  const dirty = useMemo(() => !mapsEqual(draft, initial), [draft, initial]);

  const save = useMutation({
    mutationFn: () => dtmfProfiles.put(
      row.node_type,
      { dtmf_mappings: draft },
      { reseller_id: resellerId, customer_id: customerId },
    ),
    onSuccess: () => { setErr(null); onSaved(); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Save failed'),
  });

  const reset = useMutation({
    mutationFn: () => dtmfProfiles.reset(
      row.node_type,
      { reseller_id: resellerId, customer_id: customerId },
    ),
    onSuccess: () => { setDraft({}); setErr(null); onSaved(); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Reset failed'),
  });

  const actionOptions = Object.values(entry.action_catalog);

  // Helper to look up the action for a key. We fall back in order:
  // draft (editing in progress) → my_override → default_map.
  function pickAction(key: string): string {
    if (key in draft) return draft[key]!;
    if (row.my_override && key in row.my_override) return row.my_override[key]!;
    return entry.default_map[key] ?? '';
  }

  function setKey(key: string, action: string) {
    setDraft((prev) => {
      const next = { ...prev };
      if (action === '' || action === 'noop') {
        delete next[key];
      } else {
        next[key] = action;
      }
      return next;
    });
  }

  const overrideCount = Object.keys(initial).length;

  return (
    <Card>
      <CardHeader
        className="cursor-pointer hover:bg-muted/40 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base">{row.label}</CardTitle>
            <CardDescription className="mt-1 flex items-center gap-2">
              <code className="text-[10px] font-mono text-muted-foreground">{row.node_type}</code>
              {overrideCount > 0 ? (
                <Badge variant="outline" className="text-[10px]">
                  {overrideCount} key{overrideCount === 1 ? '' : 's'} overridden
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] text-muted-foreground">
                  Using {viewerTier === 'owner' ? 'code default' : viewerTier === 'reseller' ? 'platform default' : 'reseller default'}
                </Badge>
              )}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs shrink-0">
            {expanded ? 'Collapse' : 'Edit keys'}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-2" role="grid" aria-label={`DTMF keys for ${row.label}`}>
            {KEYPAD.flatMap((r) => r).map((key) => {
              const current = pickAction(key);
              const isOverride = key in draft || (row.my_override && key in row.my_override);
              const defaultAction = entry.default_map[key];
              const hasDefault = Boolean(defaultAction);
              return (
                <div key={key} className={`rounded-md border p-2 space-y-1 ${isOverride ? 'border-primary/40 bg-primary/5' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xl font-bold w-8 text-center">{key}</div>
                    {isOverride && <span className="text-[9px] uppercase tracking-wider text-primary">custom</span>}
                  </div>
                  <Select
                    value={current || 'noop'}
                    onValueChange={(v) => setKey(key, v === 'noop' && !hasDefault ? '' : v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="No action" />
                    </SelectTrigger>
                    <SelectContent>
                      {hasDefault && (
                        <SelectItem value="noop" className="text-xs italic text-muted-foreground">
                          (no action — override default)
                        </SelectItem>
                      )}
                      {!hasDefault && (
                        <SelectItem value="noop" className="text-xs italic text-muted-foreground">
                          No action
                        </SelectItem>
                      )}
                      {actionOptions.map((opt) => (
                        <SelectItem key={opt.action} value={opt.action} className="text-xs">
                          <span className="font-medium">{opt.label}</span>
                          {opt.description && (
                            <span className="block text-[10px] text-muted-foreground">{opt.description}</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {defaultAction && !isOverride && (
                    <p className="text-[10px] text-muted-foreground truncate">
                      default → {entry.action_catalog[defaultAction]?.label ?? defaultAction}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {err && <p role="alert" className="text-xs text-destructive">{err}</p>}

          <div className="flex items-center justify-between gap-2 pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              Effective source: <span className="font-medium">{SOURCE_LABEL(row.source)}</span>
            </p>
            <div className="flex gap-2">
              {overrideCount > 0 && (
                <Button
                  variant="ghost" size="sm" className="h-8 text-xs"
                  onClick={() => reset.mutate()}
                  disabled={reset.isPending || save.isPending}
                >
                  {reset.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                  Reset to default
                </Button>
              )}
              <Button
                size="sm" className="h-8 text-xs"
                onClick={() => save.mutate()}
                disabled={!dirty || save.isPending}
              >
                {save.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function mapsEqual(a: DtmfMap, b: DtmfMap): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
}
